import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  PlaneTakeoff, ShieldCheck, Clock, Star, ArrowRight, CheckCircle2,
  Users, Plus, Minus, Sparkles, MapPin,
} from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import BookingWidget from "@/components/BookingWidget";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroImg from "@/assets/hero-transfers.jpg";

interface Vehicle {
  id: string; name: string; capacity: number;
  image_url: string | null; description: string | null; badge: string | null;
}

interface RoutePage {
  id: string; slug: string; title: string;
  meta_description: string | null; hero_image_url: string | null;
}

const ROUTES_PER_PAGE = 6;

const steps = [
  { icon: MapPin, title: "Tell us where", text: "Pick your route, dates, and how many travelers." },
  { icon: ShieldCheck, title: "We confirm", text: "Instant confirmation with driver details and price." },
  { icon: PlaneTakeoff, title: "We meet you", text: "Your chauffeur is waiting — flight tracked, name sign in hand." },
];

const features = [
  { icon: ShieldCheck, title: "Vetted drivers", text: "English-speaking, professional, and discreet." },
  { icon: Clock, title: "Always on time", text: "24/7 dispatch and live flight tracking." },
  { icon: Sparkles, title: "Premium fleet", text: "Late-model SUVs, vans, and luxury vehicles." },
  { icon: Star, title: "Five-star service", text: "Hundreds of glowing reviews from real travelers." },
];

const faqs = [
  { q: "How early should I book?", a: "We recommend booking at least 24 hours in advance. We can usually accommodate same-day requests too — just contact us." },
  { q: "What if my flight is delayed?", a: "We track every flight in real time. Your driver will adjust the pickup time automatically — no extra cost." },
  { q: "Do you offer child seats?", a: "Yes — child and booster seats are available on request at no extra cost." },
  { q: "Can I cancel my booking?", a: "Free cancellation up to 24 hours before your scheduled pickup." },
  { q: "Which payment methods do you accept?", a: "Credit card, PayPal, or cash to the driver — your choice at checkout." },
];

const TransfersPage = () => {
  usePageSeo("transfers");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [routes, setRoutes] = useState<RoutePage[]>([]);
  const [routePage, setRoutePage] = useState(1);

  useEffect(() => {
    supabase.from("vehicles")
      .select("id,name,capacity,image_url,description,badge,sort_order")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .limit(3)
      .then(({ data }) => setVehicles((data as Vehicle[]) || []));

    supabase.from("landing_pages")
      .select("id,slug,title,meta_description,hero_image_url,sort_order")
      .eq("is_published", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => setRoutes((data as RoutePage[]) || []));
  }, []);

  const totalRoutePages = Math.max(1, Math.ceil(routes.length / ROUTES_PER_PAGE));
  const pagedRoutes = routes.slice(
    (routePage - 1) * ROUTES_PER_PAGE,
    routePage * ROUTES_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO with embedded booking widget */}
      <section className="relative min-h-[100svh] flex items-center pt-24 overflow-hidden isolate">
        <div className="absolute inset-0 z-0">
          <img src={heroImg} alt="Premium private transfer in the Dominican Republic" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--hero-bg))]/85 via-[hsl(var(--hero-bg))]/55 to-[hsl(var(--hero-bg))]/85" />
        </div>
        <div className="relative z-10 container mx-auto px-4 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 text-white animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
              <PlaneTakeoff className="w-3.5 h-3.5 text-primary-glow" /> Private Transfers
            </span>
            <h1 className="font-display font-semibold text-5xl md:text-6xl leading-[1.05] mt-6">
              From the airport to <span className="italic text-gradient-ocean">your sunset</span>.
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">
              Door-to-door private transfers across Punta Cana, La Romana, Samaná, Santo Domingo and beyond. Tracked flights, fixed prices, zero stress.
            </p>
            <div className="mt-8 flex items-center gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary-glow" /> Free cancellation</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary-glow" /> Meet & greet</div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="glass rounded-3xl p-2 shadow-elegant">
              <div className="rounded-2xl bg-card p-1">
                <BookingWidget />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container mx-auto px-4 py-24">
        <div className="max-w-2xl mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">How it works</span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">Three steps. No surprises.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.title} className="relative bg-gradient-card rounded-3xl p-8 shadow-soft border border-border/60">
              <div className="absolute top-6 right-6 font-display text-5xl font-semibold text-primary/15">0{i + 1}</div>
              <div className="w-12 h-12 rounded-xl bg-gradient-ocean text-primary-foreground flex items-center justify-center shadow-glow">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl font-semibold mt-5">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-gradient-soft">
        <div className="container mx-auto px-4 py-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <div key={f.title} className="bg-card rounded-2xl p-6 shadow-soft border border-border/60 hover:shadow-elegant transition-smooth">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-lg font-semibold mt-4">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET PREVIEW */}
      {vehicles.length > 0 && (
        <section className="container mx-auto px-4 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div className="max-w-xl">
              <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">Our fleet</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">Choose your ride.</h2>
            </div>
            <Link to="/fleet" className="group inline-flex items-center gap-2 text-primary font-semibold">
              View full fleet <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {vehicles.map((v) => (
              <div key={v.id} className="bg-card rounded-3xl shadow-card border border-border/60 overflow-hidden hover:shadow-elegant transition-smooth">
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  {v.image_url ? (
                    <img src={v.image_url} alt={v.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-ocean" />
                  )}
                </div>
                <div className="p-5">
                  {v.badge && <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-accent font-semibold">{v.badge}</span>}
                  <h3 className="font-display text-xl font-semibold mt-1">{v.name}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                    <Users className="w-4 h-4" /> Up to {v.capacity}
                  </div>
                  {v.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{v.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* POPULAR ROUTES */}
      {routes.length > 0 && (
        <section className="bg-gradient-soft">
          <div className="container mx-auto px-4 py-24">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
              <div className="max-w-xl">
                <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">Popular routes</span>
                <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
                  Where do you need to <span className="text-gradient-ocean">go?</span>
                </h2>
                <p className="text-muted-foreground mt-4">
                  Fixed-price private transfers for the most-requested destinations across the Dominican Republic.
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                {routes.length} route{routes.length === 1 ? "" : "s"} available
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pagedRoutes.map((r) => (
                <Link
                  key={r.id}
                  to={`/transfers/${r.slug}`}
                  className="group bg-card rounded-3xl shadow-card border border-border/60 overflow-hidden hover:shadow-elegant transition-smooth flex flex-col"
                >
                  <div className="aspect-[16/10] bg-muted overflow-hidden">
                    {r.hero_image_url ? (
                      <img
                        src={r.hero_image_url}
                        alt={r.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-ocean" />
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-primary mt-1 shrink-0" />
                      <h3 className="font-display text-lg font-semibold leading-snug">{r.title}</h3>
                    </div>
                    {r.meta_description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{r.meta_description}</p>
                    )}
                    <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                      View details
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {totalRoutePages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setRoutePage((p) => Math.max(1, p - 1))}
                  disabled={routePage === 1}
                  className="h-10 px-4 rounded-full border border-border bg-card text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition"
                  aria-label="Previous page"
                >
                  Previous
                </button>
                {Array.from({ length: totalRoutePages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setRoutePage(n)}
                    className={`h-10 w-10 rounded-full text-sm font-semibold transition ${
                      n === routePage
                        ? "bg-gradient-ocean text-primary-foreground shadow-glow"
                        : "bg-card border border-border hover:bg-muted"
                    }`}
                    aria-current={n === routePage ? "page" : undefined}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setRoutePage((p) => Math.min(totalRoutePages, p + 1))}
                  disabled={routePage === totalRoutePages}
                  className="h-10 px-4 rounded-full border border-border bg-card text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition"
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="container mx-auto px-4 py-24 max-w-3xl">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">FAQ</span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
            Questions, answered.
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border border-border/60 bg-card rounded-2xl px-5 shadow-soft">
              <AccordionTrigger className="font-display font-semibold text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <Footer />
    </div>
  );
};

export default TransfersPage;
